import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Download, Smartphone, Clock, CheckCircle2, HelpCircle } from "lucide-react";
import { Card, PageHeader } from "../../components/dash/Kit";

const PLATFORMS = {
  Instagram: {
    where: "the Instagram app on your phone",
    steps: [
      "Open PostMint on your phone, go to Publishing and find the approved post.",
      "Tap “Download photo” to save the image to your phone, then tap “Copy caption”.",
      "Open Instagram, tap + and choose Post (or Reel for a video).",
      "Pick the photo you just downloaded, then continue to the caption screen.",
      "Tap the caption field and paste. Check it reads well, then add your location.",
      "Tap Share. Come back to PostMint and mark the post as published so your calendar stays accurate.",
    ],
    tips: [
      "Instagram doesn't allow scheduling from the mobile app for personal accounts — Business accounts can schedule in Meta Business Suite.",
      "Reels: film the shots in the reel script, then use the voiceover text as your spoken script.",
      "Keep the first line of your caption strong — it's the only part most people read.",
    ],
  },
  Facebook: {
    where: "your Facebook Page (desktop is easiest)",
    steps: [
      "In PostMint, open Publishing and copy the caption for the Facebook post.",
      "Download the photo if the post has one.",
      "Go to your Facebook Page and click “Create post”.",
      "Paste the caption, add the photo, and add a link if the post mentions one.",
      "Either post now, or click the arrow next to Post and choose “Schedule post” for the recommended time.",
      "Mark it as published in PostMint.",
    ],
    tips: [
      "Meta Business Suite lets you schedule Facebook and Instagram together, which saves time if you post to both.",
      "Facebook shows link previews — paste your link, wait for the preview to load, then delete the raw URL from the text.",
    ],
  },
  LinkedIn: {
    where: "your LinkedIn page or profile (desktop)",
    steps: [
      "Copy the LinkedIn version of the caption from PostMint — it's written in a more professional tone than the Instagram one.",
      "Download the image if there is one.",
      "On LinkedIn, click “Start a post”.",
      "Paste the caption and add the image.",
      "Add 3–5 relevant hashtags at the end if they aren't already included.",
      "Click Post, then mark it as published in PostMint.",
    ],
    tips: [
      "LinkedIn rewards posts that keep people on the platform — put links in the first comment if reach matters more than clicks.",
      "Posting from a company page and then sharing it from your personal profile usually reaches more people.",
    ],
  },
  TikTok: {
    where: "the TikTok app on your phone",
    steps: [
      "Film the shots from the reel script in PostMint — each shot is a few seconds long.",
      "Open TikTok, tap +, then upload your clips or record directly.",
      "Add the on-screen text from the script, and choose a sound close to the suggestion in the script.",
      "Copy the caption from PostMint and paste it into the description.",
      "Post, then mark it as published in PostMint.",
    ],
    tips: [
      "The hook matters more than the production quality — say it in the first two seconds.",
      "Vertical video only, and keep it under 30 seconds unless the script says otherwise.",
    ],
  },
};

export default function HowToPost() {
  const [tab, setTab] = useState("Instagram");
  const p = PLATFORMS[tab];

  return (
    <div className="space-y-6" data-testid="how-to-post-page">
      <PageHeader
        testId="how-to-post-header"
        title="How to post your content"
        body="PostMint writes, plans and organises everything. For now you publish with two taps — copy the caption, download the image, paste it into the app. This page shows you exactly how, per platform."
      />

      <Card testId="how-to-post-why">
        <p className="font-display text-lg font-semibold text-slate-900">Why you post it yourself (for now)</p>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          Instagram, Facebook, LinkedIn and TikTok only allow automatic publishing through an approved developer app.
          That approval is in progress. Until it's granted, PostMint does everything except the final tap: your content is
          written, planned, approved and waiting with the caption and image ready to copy. Nothing about your calendar or
          approvals changes when automatic publishing switches on — you'll simply stop needing this page.
        </p>
      </Card>

      <Card testId="how-to-post-flow">
        <p className="font-display text-lg font-semibold text-slate-900">The short version</p>
        <ol className="mt-4 space-y-3">
          {[
            [CheckCircle2, "Approve", "Open My Content, read the draft, edit anything you want and approve it."],
            [Clock, "Check the time", "In Publishing, each approved post has a recommended time. Post around then."],
            [Copy, "Copy the caption", "One tap copies the caption and hashtags together."],
            [Download, "Download the image", "One tap saves the photo or graphic to your device."],
            [Smartphone, "Paste and post", "Open the platform, paste, attach, post. Roughly 30 seconds per post."],
          ].map(([Icon, title, body], i) => (
            <li key={title} className="flex gap-3">
              <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#FFF3EF] text-xs font-bold text-[#F95738]">
                {i + 1}
              </span>
              <span>
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <Icon className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
                  {title}
                </span>
                <span className="mt-0.5 block text-sm text-slate-600">{body}</span>
              </span>
            </li>
          ))}
        </ol>
        <Link
          to="/dashboard/publishing"
          data-testid="how-to-post-publishing-link"
          className="mt-5 inline-flex rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800"
        >
          Go to Publishing
        </Link>
      </Card>

      <Card testId="how-to-post-platforms">
        <p className="font-display text-lg font-semibold text-slate-900">Step by step, per platform</p>
        <div className="mt-4 flex flex-wrap gap-2" role="tablist" aria-label="Platform">
          {Object.keys(PLATFORMS).map((name) => (
            <button
              key={name}
              type="button"
              role="tab"
              aria-selected={tab === name}
              onClick={() => setTab(name)}
              data-testid={`how-to-post-tab-${name.toLowerCase()}`}
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                tab === name
                  ? "border-[#F95738] bg-[#FFF3EF] text-[#B93615]"
                  : "border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
            >
              {name}
            </button>
          ))}
        </div>

        <p className="mt-5 text-sm text-slate-500">Easiest in {p.where}.</p>
        <ol className="mt-3 space-y-2.5" data-testid={`how-to-post-steps-${tab.toLowerCase()}`}>
          {p.steps.map((s, i) => (
            <li key={s} className="flex gap-3 text-sm leading-relaxed text-slate-700">
              <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-xs font-bold text-slate-500">
                {i + 1}
              </span>
              <span>{s}</span>
            </li>
          ))}
        </ol>

        <div className="mt-5 rounded-2xl bg-[#FAFAFA] p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Tips for {tab}</p>
          <ul className="mt-2 space-y-1.5">
            {p.tips.map((t) => (
              <li key={t} className="text-sm text-slate-600">
                · {t}
              </li>
            ))}
          </ul>
        </div>
      </Card>

      <Card testId="how-to-post-help">
        <p className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900">
          <HelpCircle className="h-4 w-4 text-slate-400" aria-hidden="true" /> Stuck on something?
        </p>
        <ul className="mt-3 space-y-2 text-sm text-slate-600">
          <li>
            · The caption didn't paste? Tap “Copy caption” again, then long-press the caption field in the app and choose
            Paste.
          </li>
          <li>· The photo looks cropped? Instagram prefers square or 4:5 images; pick the reframe option before posting.</li>
          <li>
            · Want a different angle or tone? Edit the draft in{" "}
            <Link to="/dashboard/content" className="font-semibold text-[#F95738] hover:underline">
              My Content
            </Link>{" "}
            or ask for a rewrite there.
          </li>
          <li>
            · Anything else —{" "}
            <Link to="/dashboard/support" className="font-semibold text-[#F95738] hover:underline">
              message support
            </Link>{" "}
            and we'll walk you through it.
          </li>
        </ul>
      </Card>
    </div>
  );
}
